/**
 * @flow
 * */
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Toaster from '@geneui/components/Toaster';

import { internetStatusSelector, appStateSelector } from 'store/selectors/applicationSelectors';
import { appSettingsUpdateSelector } from 'store/selectors/appSettingsSelectors';
import useContainerStatusHandler from 'hooks/useContainerStatusHandler';
import { allFiltersSelector } from 'store/selectors/filterSelectors';
import InternetStatusListener from 'hooks/internetStatusListener';
import HeaderContainer from 'views/containers/HeaderContainer';
import { detectAbsentUrl } from 'lib/helpers/detectAbsentUrl';
import CacheService from 'core/services/storage/CacheService';
import { pdfConfig } from 'config/settings/indexDBConfig';
import Skeleton from 'views/components/loaders/Skeleton';
import useMessageHandler from 'hooks/useMessageHandler';
import { routes, staticRoutes } from 'config/routes';
import messages from 'config/settings/messages';
import { history } from 'config/history';
import className from 'classnames';
import config from 'config';

import AppLayout from '../AppLayout';
import Routes from '../Router';

import './styles.scss';

const allRoutes = [...staticRoutes, ...routes];

const AppContainer = () => {
  const [refreshKey, setRefreshKey] = useState(null);
  const liveCasinoToken = CacheService.getItem('live_casino_token');

  const [fullScreen, setFullScreen] = useState(null);
  const internetStatus = useSelector(internetStatusSelector);
  const appState = useSelector(appStateSelector);
  const location = useLocation();
  InternetStatusListener();

  const {
    isSucceed
  } = useContainerStatusHandler({ selector: appSettingsUpdateSelector });

  const {
    isFailed
  } = useContainerStatusHandler({ selector: allFiltersSelector });

  useMessageHandler({
    errorMessage: messages.error.somethingWentWrong,
    isFailed
  });

  const versionUpdateHandler = useCallback(() => {
    const currentVersion = config.buildVersion;
    const storedVersion = localStorage.getItem('app_version');

    if (storedVersion !== 'undefined' && storedVersion !== currentVersion) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!['auth_token', 'Fingerprint'].includes(key)) {
          // $FlowFixMe
          localStorage.removeItem(key);
        }
      }
      // $FlowFixMe
      const request = indexedDB.open('DB', 1);
      request.onsuccess = function (event) {
        const db = event.target.result;
        const transaction = db.transaction(
            pdfConfig.objectStoresMeta.map(meta => meta.store),
            'readwrite'
        );

        pdfConfig.objectStoresMeta.forEach(meta => {
          const storeName = meta.store;
          const objectStore = transaction.objectStore(storeName);
          objectStore.clear();
        });

        transaction.oncomplete = () => {
          db.close();
        };
      };
      localStorage.setItem('app_version', currentVersion);
    } else if (!storedVersion || storedVersion === 'undefined') {
      localStorage.setItem('app_version', currentVersion);
    }
  }, [config]);

  const messageHandler = useCallback(() => {
    if (internetStatus.isOnLine !== null) {
      if (internetStatus.isOnLine) {
        Toaster.success({
          title: 'Welcome Back',
          toasterPosition: 'left-top',
          message: internetStatus.onLineMessage
        });
      } else {
        Toaster.warning({
          title: 'Warning',
          toasterPosition: 'top-center',
          duration: 10000,
          message: internetStatus.offLineMessage
        });
      }
    }
  }, [internetStatus.isOnLine, internetStatus.onLineMessage, internetStatus.offLineMessage]);

  const matchedRoute = detectAbsentUrl(location, allRoutes);
  const hideHeader = !!matchedRoute?.fullScreen;

  const checkUrl = useCallback(() => {
    const existing = detectAbsentUrl(location, allRoutes);
    !existing && history.push('/404');
    setFullScreen(!!existing.fullScreen);
  }, [location, setFullScreen]);

  const resetDom = useCallback(() => {
    if (isSucceed) {
      setRefreshKey(Date.now().toString());
    }
  }, [isSucceed]);

  useEffect(messageHandler, [internetStatus]);
  useEffect(versionUpdateHandler, []);
  useEffect(checkUrl, [location]);
  useEffect(resetDom, [isSucceed]);

  return (
    <>
      {!liveCasinoToken && appState.isLoggedIn && !hideHeader && <HeaderContainer />}
      <Switch>
        <Suspense
          fallback={<Skeleton center />}
        >
          {fullScreen !== null && (
            <div
              key={refreshKey}
              className={`main-wr flex ${className({ 'full-screen': fullScreen })}`}
            >
              <main className="main-content">
                <div className="right-main-container">
                  <Routes routes={staticRoutes} />
                  {/* // $FlowFixMe */}
                  {routes.map(({ page, navigation, name, ...rest }, index) => (
                    <Route
                      /* eslint-disable-next-line react/no-array-index-key */
                      key={index}
                      {...rest}
                      render={() => (
                        <AppLayout
                          page={page}
                          navigation={navigation}
                        />
                      )}
                    />
                  ))}
                </div>
              </main>
            </div>
          )}
        </Suspense>
      </Switch>
      <Toaster
        toasterPosition="right-top"
        notificationPosition="right-top"
      />
    </>
  );
};

export default AppContainer;
